import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect } from 'react';
// material-ui
import { Container, Box, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import Header from './Header';
import Footer from './Footer';
import Sidebar from './Sidebar';
import { useElectricPage } from '../../helpers/isElectricPage';
import { ROUTES } from '../../constants';
import { useToast } from '../../hooks/ToastProvider';
import useGlobalMutation from '../../hooks/useGlobalMutation';
import { DELETE_PROFILE } from '../../redux/_user.redux';
import { clearReturnPath, getReturnPath } from '../../helpers/routeHistory';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../api/AuthApi';
import { getContracts } from '../../api/ElectricApi';
import FloatingBear from '../../components/login/FloatingBear';
import ChatWidget from '../../components/chatbot/ChatWidget';
import { canShowChatbot } from '../../components/chatbot/chatbotVisibility';
import { isBusinessAccount } from '../../helpers/siteManual';
import GlobalNotification from '../../components/login/GlobalNotification';

const chatbotShowWithoutLogin = process.env.NODE_ENV === 'development' && process.env.REACT_APP_CHATBOT_SHOW_WITHOUT_LOGIN === 'true';

//-----------------------|| MAIN LAYOUT ||-----------------------//

const MainLayout = ({ children, isNotPublicPage }) => {
    const { showToast } = useToast();
    const dispatch = useDispatch();
    const history = useHistory();
    const isElectricPage = useElectricPage();
    const location = useLocation();
    const isLoginPage = location.pathname === ROUTES.LOGIN;
    const isForgetPasswordPage = location.pathname === ROUTES.FORGET_PASSWORD || location.pathname === ROUTES.FORGET_PASSWORD_SENT;
    const shouldHideFooter = isLoginPage || isForgetPasswordPage;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const profile = useSelector((state) => state.user.profile);
    // On signed-in pages the bear waits for the profile: only 法人 customers
    // get it (個人 have the sidebar banner), so a 個人 customer never sees it
    // appear and then vanish. Other pages, such as login, keep it as before.
    const showsFloatingBear = !isElectricPage || isBusinessAccount(profile);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const logoutMutation = useGlobalMutation(logout, {
        onSuccess: async () => {
            ['token', 'admin_token', 'target_user_id', 'auth.token', 'auth.refreshToken', 'auth.credential'].forEach((k) =>
                localStorage.removeItem(k)
            );
            dispatch({ type: DELETE_PROFILE });
            clearReturnPath();
            history.push(ROUTES.LOGIN);
        },
        onError: () => showToast()
    });

    const { data: contractsResponse } = useQuery(['getContracts'], () => getContracts(1, 1000), {
        enabled: isElectricPage
    });
    const hasDrContract = useMemo(() => {
        const list = contractsResponse?.data ?? [];
        return list.some((c) => c.drValidFlag === true);
    }, [contractsResponse]);

    // const isElectricHome = location.pathname === ROUTES.ELECTRIC.BASE || location.pathname === ROUTES.ELECTRIC.BASE + '/';
    // const isWidePage =
    //     [ROUTES.ELECTRIC.BILLING, ROUTES.ELECTRIC.DR_SCHEDULE].includes(location.pathname) || (isElectricHome && hasDrContract);

    const containerStyle = {
        width: '100%',
        maxWidth: 900,
        padding: 0,
        height: '100%'
    };

    const onLogout = () => {
        logoutMutation.mutate();
    };

    useEffect(() => {
        const token = localStorage.getItem('token');

        if (token && (isLoginPage || isForgetPasswordPage)) {
            const returnPath = getReturnPath();
            const redirectPath = returnPath || ROUTES.ELECTRIC.BASE;
            history.push(redirectPath);
        }
    }, [isLoginPage, isForgetPasswordPage, history]);

    const token = localStorage.getItem('token');
    if (token && (isLoginPage || isForgetPasswordPage)) {
        return null;
    }
    return (
        <Box style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header isNotPublicPage={isNotPublicPage} onMenuClick={handleDrawerToggle} mobileOpen={mobileOpen} onLogout={onLogout} />
            <Toolbar />

            <Box style={{ display: 'flex', flex: 1 }}>
                <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} onLogout={onLogout} />
                <Box
                    sx={{
                        backgroundColor: '#f2f9fc',
                        flex: 1,
                        display: 'flex',
                        padding: '16px',
                        paddingLeft: { xs: '16px', sm: '19px' },
                        marginLeft: isElectricPage && !isMobile ? '250px' : '0',
                        minWidth: 0,
                        overflow: 'visible'
                    }}
                >
                    {!isMobile && isElectricPage && <Box style={{ flex: 1 }} />}
                    <Container maxWidth={false} style={containerStyle} sx={{ overflow: 'visible' }}>
                        {children}
                    </Container>
                    {!isMobile && isElectricPage && <Box style={{ flex: 2 }} />}
                </Box>
            </Box>
            {/* Personal (個人) customers open the manual from the sidebar banner
                instead (PPS-399); 法人 customers keep the floating bear. */}
            {showsFloatingBear && <FloatingBear isLoginPage={isLoginPage} />}
            {/*
                The widget belongs on signed-in pages. The escape hatch is for
                local work only: logging in needs the portal API, which needs
                Salesforce credentials to run locally, so without it the widget
                cannot be seen at all. It is honoured
                only under `npm start` -- a build always has NODE_ENV set to
                production, so a stray env var cannot put the widget on the
                login page of a deployed portal.
            */}
            {/*
                isNotPublicPage alone is not "signed in": MainRoutes also sets it
                on /change_password for visitors arriving from an email link.
                The widget needs the portal token -- without one every question
                would end in "log in again". Personal (個人) customers only (PPS-389).
            */}
            {canShowChatbot({ isNotPublicPage, token, profile, showWithoutLogin: chatbotShowWithoutLogin }) && (
                <ChatWidget aboveBear={showsFloatingBear} />
            )}
            <GlobalNotification />
            {!shouldHideFooter && <Footer isElectricPage={isElectricPage} isMobile={isMobile} />}
        </Box>
    );
};

MainLayout.propTypes = {
    children: PropTypes.node
};

export default MainLayout;
